/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {useCallback, useRef} from 'react';
import * as geometryEngine from '@arcgis/core/geometry/geometryEngine';
import {CVSAssetType} from '@ion/core/src/types/entities';
import {FLEET_ASSET_LAYER_FIELDS} from '../constants';
import {createGraphics} from '../helpers/LayerUtils';

/* -------------------------------------------------------------------------- */
/* Types                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Telemetry point extended with eventTime.
 *
 * `eventTime` (epoch ms) is the authoritative source used to:
 *  - Calculate animation duration
 *  - Determine animation gaps
 *  - Drive per-asset speed and timing
 */
export type Point = CVSAssetType & {
  eventTime: number;
};

/**
 * Runtime animation state tracked per asset.
 *
 * Allows:
 *  - Independent animation per asset (Per-Asset Dynamic Animation)
 *  - Seamless recalculation when new telemetry arrives mid-animation (Recalculation)
 */
type ActiveAnimation = {
  stop: () => void;
  currentPosition: Point; // Live animated position
  lastEventTime: number;
  speed: number; // meters / second
  distance: number; // meters

  /** Track animation progress */
  currentIndex: number;
  totalSteps: number;

  /** Remaining points not yet animated (both previous and new points) */
  remainingPoints: Point[];
};

/* -------------------------------------------------------------------------- */
/* Constants                                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Maximum telemetry gap allowed for animation.
 *
 * If the time difference between the first and last telemetry point
 * exceeds this value, animation is skipped (Animation Gap Handling)
 */
const MAX_ANIMATION_GAP_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Minimum distance used when densifying polylines.
 * Prevents overly small geometry segments.
 */
const MIN_SEGMENT_METERS = 1;

/**
 * Duration of a single animation step.
 *
 * - One step represents ONE SECOND of telemetry time
 * - STEP_DURATION controls *when* each step is applied
 */
const STEP_DURATION = 1000;

/* -------------------------------------------------------------------------- */
/* Geometry Helpers                                                           */
/* -------------------------------------------------------------------------- */

/**
 * Builds a WGS84 polyline from telemetry points.
 * Used for interpolating positions along asset path.
 */
const buildPolylineFromPoints = (points: Point[]) => ({
  type             : 'polyline',
  paths            : [points.map(p => [p.longitude, p.latitude])],
  spatialReference : {wkid: 4326}
});

/**
 * Calculates geodesic distance (meters) of a polyline.
 */
const getDistanceMeters = (line: __esri.Polyline) =>
  geometryEngine.geodesicLength(line, 'meters');

/**
 * Densifies a polyline so that segments do not exceed `segmentLength`.
 * Ensures smooth intermediate positions for animation.
 */
const densifyLine = (line: __esri.Polyline, segmentLength: number) =>
  geometryEngine.geodesicDensify(line, segmentLength, 'meters').paths[0];

/**
 * Finds the nearest *real* telemetry point to a given interpolated position.
 * Preserves telemetry fields like speed, status while animating interpolated coordinates.
 */
const getNearestRealPoint = (
  lon: number,
  lat: number,
  realPoints: Point[]
): Point => {
  let nearest = realPoints[0];
  let minDistSq = Infinity;

  for (const p of realPoints) {
    const dx = p.longitude - lon;
    const dy = p.latitude - lat;
    const distSq = dx * dx + dy * dy;

    if (distSq < minDistSq) {
      minDistSq = distSq;
      nearest = p;
    }
  }

  return nearest;
};

/**
 * Normalizes ArcGIS coordinate formats to [lon, lat].
 */
const getLonLat = p => {
  if (p) {
    return Array.isArray(p) ? [p[0], p[1]] : [p.x, p.y];
  }
  return [null, null];
};

/* -------------------------------------------------------------------------- */
/* Hook: useAnimationAssetToPosition                                         */
/* -------------------------------------------------------------------------- */

/**
 * Hook: useAnimationAssetToPosition
 *
 * Animates assets smoothly along incoming telemetry points while meeting
 * critical requirements for real-time asset tracking.
 *
 * --------------------------------------------------------------------------
 * Requirements Addressed:
 * --------------------------------------------------------------------------
 *
 * 1) Animation Gap Handling (Special Case)
 *    - If the time difference between the current position and next telemetry point
 *      exceeds 5 minutes, the hook will skip animation and immediately update
 *      the asset’s position to the last reported location.
 *    - Ensures assets do not animate unrealistically over large gaps.
 *
 * 2) Per-Asset Dynamic Animation
 *    - Each asset has an independent animation instance with its own speed and duration.
 *    - Speed is dynamically calculated as: speed = distance / duration
 *    - Duration is derived from telemetry timestamps (T seconds between points)
 *    - Polyline interpolation and densification guarantee smooth motion.
 *
 * 3) Recalculation on New Position Updates
 *    - If a new telemetry update is received mid-animation:
 *        • Current animation is stopped
 *        • The current animated position becomes the new starting point
 *        • Distance, speed, and duration are recalculated based on the new target
 *        • Animation resumes seamlessly from current position to new destination
 *
 * --------------------------------------------------------------------------
 * Key Features of the Hook:
 * --------------------------------------------------------------------------
 *
 * - Independent per-asset animation stored in a Map (activeAnimationsRef)
 * - Uses requestAnimationFrame for smooth step-based updates
 * - Interpolates intermediate positions from densified polyline for smooth animation
 * - STEP_DURATION defines how often each animation step is applied
 * - Maintains remainingPoints to allow seamless continuation when new telemetry arrives
 *
 * Usage:
 * - Call animateAssetPosition(assetId, points) to animate an asset
 * - Call stopAssetAnimation(assetId) or stopAllAssetAnimation() to stop animations
 */
export const useAnimationAssetToPosition = (fnCallback: Function) => {
  /**
   * Tracks active animations by asset ID.
   * Used to stop/recalculate animation if new data arrives.
   */
  const activeAnimationsRef = useRef<Map<string, ActiveAnimation>>(new Map());

  /* ------------------------------------------------------------------------ */
  /* Polyline-based Animation Engine                                          */
  /* ------------------------------------------------------------------------ */
  
  /**
   * Core function to animate an asset along a polyline.
   *
   * Requirements covered:
   * - Per-Asset Dynamic Animation: speed and duration calculated per asset
   * - Deterministic step execution: STEP_DURATION per second
   */
  const animateAlongPolyline = useCallback((
    assetId: string,
    polylinePoints: Point[],
    durationSec: number
  ): ActiveAnimation => {
    let animationId: number;
    let startTime: number | null = null;
    let stopped = false;

    const stop = () => {
      stopped = true;
      if (animationId) cancelAnimationFrame(animationId);
    };

    /** 
     * Number of animation steps (one per telemetry second)
     */
    const steps = Math.ceil(durationSec);

    /**
     * Build and densify the movement polyline
     * - Ensures smooth animation
     */
    const line          = buildPolylineFromPoints(polylinePoints) as __esri.Polyline;
    const totalDistance = getDistanceMeters(line);

    /**
     * Determine segment length for densification
     */
    const segmentLength = Math.max(
      totalDistance / (steps - 1),
      MIN_SEGMENT_METERS
    );

    const densified       = densifyLine(line, segmentLength);
    const densifiedCoords = densified.slice(0, densified.length);

    /**
     * Sample exactly `steps` evenly spaced positions along densified polyline
     * - Preserves non-spatial telemetry fields
     */
    const stepInterval = (densifiedCoords.length - 1) / (steps - 1);
    const intermediatePositions: Point[] = [];

    for (let i = 0; i < steps; i++) {
      const idx         = Math.round(i * stepInterval);
      const [lon, lat]  = getLonLat(densifiedCoords[idx]);
      const nearestReal = getNearestRealPoint(lon, lat, polylinePoints);

      intermediatePositions.push({
        ...nearestReal,
        longitude : lon,
        latitude  : lat,
        // Synthetic timestamp for smooth animation
        eventTime : polylinePoints[0].eventTime + i * 1000
      });
    }

    let currentIndex = 0;

    /**
     * Animation loop driven by elapsed time, not frame count
     */
    const frame = (timestamp: number) => {
      if (stopped) return;
      if (!startTime) startTime = timestamp;

      const elapsed = timestamp - startTime;

      if (currentIndex < intermediatePositions.length) {
        if (elapsed >= STEP_DURATION * (currentIndex + 1)) {
          const currentPosition = intermediatePositions[currentIndex];

          const graphics = createGraphics(
            [currentPosition],
            FLEET_ASSET_LAYER_FIELDS
          ) as any;

          fnCallback(Number(assetId), graphics);

          const active = activeAnimationsRef.current.get(assetId);
          if (active) {
            active.currentPosition = currentPosition;
            active.currentIndex    = currentIndex;
            // Update remaining points (everything after currentIndex)
            active.remainingPoints = intermediatePositions.slice(currentIndex + 1);
          }

          // ✅ FINAL STEP — animation complete
          if (currentIndex === intermediatePositions.length - 1) {
            activeAnimationsRef.current.delete(assetId);
            return; // stop scheduling frames
          }

          currentIndex++;
        }

        animationId = requestAnimationFrame(frame);
      }
    };

    animationId = requestAnimationFrame(frame);

    return {
      currentPosition : intermediatePositions[0],
      speed           : totalDistance / durationSec, // Per-Asset Dynamic Animation: speed = distance / duration
      remainingPoints : intermediatePositions.slice(1),
      lastEventTime   : polylinePoints[polylinePoints.length - 1].eventTime,
      distance        : totalDistance,
      totalSteps      : steps,
      currentIndex    : 0,
      stop
    };
  }, [fnCallback]);

  /* ------------------------------------------------------------------------ */
  /* Asset Animation Entry Point                                              */
  /* ------------------------------------------------------------------------ */
  
  /**
   * Public API to animate an asset given new telemetry points.
   *
   * Requirements covered:
   * - Animation Gap Handling: skips animation if > 5 min
   * - Per-Asset Dynamic Animation: speed/duration calculated per asset
   * - Recalculation: uses current animated position as start if animation exists
   */
  const animateAssetPosition = useCallback((assetId: string, incomingPoints: Point[]) => {
    if (!incomingPoints || incomingPoints.length < 2) return;

    const existing = activeAnimationsRef.current.get(assetId);

    let startPoint: Point;
    let pointsToAnimate: Point[];

    if (existing) {
      // Stop current animation
      existing.stop();

      const animatedPoint = existing.currentPosition;

      // Get remaining points from previous animation
      const previousRemaining = existing.remainingPoints.filter(
        p => p.eventTime > animatedPoint.eventTime
      );

      // Combine into full animation path
      pointsToAnimate = [animatedPoint, ...previousRemaining, ...incomingPoints];
      startPoint      = animatedPoint;
    } else {
      // No existing animation, just animate new points
      pointsToAnimate = [...incomingPoints].sort((a, b) => a.eventTime - b.eventTime);
      startPoint      = pointsToAnimate[0];
    }

    /**
     * Total duration of animation in milliseconds
     */
    const totalDurationMs =
      pointsToAnimate[pointsToAnimate.length - 1].eventTime - startPoint.eventTime;

    /**
     * Animation Gap Handling (Special Case)
     * - Skip animation if duration invalid or gap > MAX_ANIMATION_GAP_MS
     * - Immediately jump asset to final reported position
     */
    if (totalDurationMs <= 0 || totalDurationMs > MAX_ANIMATION_GAP_MS) {
      const graphics = createGraphics(
        [pointsToAnimate[pointsToAnimate.length - 1]],
        FLEET_ASSET_LAYER_FIELDS) as any;

      fnCallback(Number(assetId), graphics);
      return;
    }

    const durationSec = totalDurationMs / 1000;
    // Animate asset along polyline with calculated speed and duration
    const animation = animateAlongPolyline(assetId, pointsToAnimate, durationSec);
    // Save active animation
    activeAnimationsRef.current.set(assetId, animation);
  }, [animateAlongPolyline, fnCallback]);

  /* ------------------------------------------------------------------------ */
  /* Stop Controls                                                            */
  /* ------------------------------------------------------------------------ */

  const stopAssetAnimation = useCallback((assetId: string) => {
    const anim = activeAnimationsRef.current.get(assetId);
    if (anim) {
      anim.stop();
      activeAnimationsRef.current.delete(assetId);
    }
  }, []);

  const stopAllAssetAnimation = useCallback(() => {
    activeAnimationsRef.current.forEach(anim => anim.stop());
    activeAnimationsRef.current.clear();
  }, []);

  /* ------------------------------------------------------------------------ */
  /* Public API                                                               */
  /* ------------------------------------------------------------------------ */

  return {
    animateAssetPosition,
    stopAllAssetAnimation,
    stopAssetAnimation
  };
};
